/**
 * Pure helpers for menu-permission route enforcement.
 *
 * `users.preferences.menu_permissions` is an array of section ids an admin has
 * granted a team member (group ids from AppSidebar.primaryMenuItems, plus the
 * standalone ids 'overview' / 'inbox'). The sidebar already HIDES ungranted
 * sections; these helpers let a route guard also BLOCK them by URL so a disabled
 * section can't be reached by typing the path.
 *
 * This file has NO imports — the AppSidebar data is injected by the thin
 * `menuPermissions.ts` binding module, so the logic stays unit-testable in
 * isolation (importing AppSidebar into a test pulls its whole component tree).
 */

export interface UrlIdEntry {
  url: string;
  id: string;
}

/**
 * Account-Settings admin "preferences" that stay strictly ADMIN-only and can
 * NEVER be unlocked for a USER/AGENT via custom menu permissions.
 *
 * This is DELIBERATELY an explicit list, NOT a generic `allowed === ['ADMIN']`
 * check — because MANY ordinary feature routes (CRM/Leads, Social Media Posts,
 * LinkedIn Posts, Automation, Commerce, Analytics, …) are also coded as
 * RoleRoute allowed=['ADMIN'] yet ARE meant to be grantable to agents through
 * the menu-permissions dialog. Only these six are true admin preferences.
 * Keep in sync with the ADMIN-only entries in AppSidebar.ROUTE_ROLE_RESTRICTIONS.
 */
export const ADMIN_ONLY_PREFERENCE_PATHS = [
  '/home/audit-log',
  '/home/billing',
  '/home/integrations',
  '/home/business-accounts',
  '/home/social-accounts',
  '/home/team-management',
  '/home/short-links',
];

/** True when `pathname` is one of the admin-only Account-Settings preferences (incl. sub-paths). */
export function isAdminPreferencePath(pathname: string): boolean {
  return ADMIN_ONLY_PREFERENCE_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

type Group = { id: string; secondaryItems?: Array<{ url?: string }> };
type Standalone = { url?: string };

/**
 * Permission id for a standalone nav item — mirrors the scheme used by
 * AppSidebar.isStandaloneItemAllowed and MemberPermissionsDialog:
 *   /home       -> 'overview'
 *   /home/inbox -> 'inbox'
 *   (anything else falls back to the url itself)
 */
export function standaloneId(url: string): string {
  if (url === '/home') return 'overview';
  if (url === '/home/inbox') return 'inbox';
  return url;
}

/**
 * Whether `pathname` is governed by sidebar `url`.
 * - '/home' (Overview) matches ONLY the dashboard landing paths — it must never
 *   prefix-match every /home/* route.
 * - Every other url matches itself exactly OR any sub-path beneath it, so deep
 *   detail routes resolve to their parent section.
 */
export function pathMatchesUrl(pathname: string, url: string): boolean {
  const base = url.split('?')[0];
  if (base === '/home') {
    return pathname === '/home' || pathname === '/home/' || pathname === '/home/agent';
  }
  return pathname === base || pathname.startsWith(base + '/');
}

/** Build the url -> permission-id lookup from the sidebar's standalone items + groups. */
export function buildUrlIdEntries(standaloneItems: Standalone[], groups: Group[]): UrlIdEntry[] {
  const entries: UrlIdEntry[] = [];
  for (const item of standaloneItems || []) {
    if (item?.url) entries.push({ url: item.url, id: standaloneId(item.url) });
  }
  for (const group of groups || []) {
    for (const sub of group.secondaryItems || []) {
      if (sub?.url) entries.push({ url: sub.url, id: group.id });
    }
  }
  return entries;
}

/**
 * Resolve the permission id governing a route. Longest-url match wins so a deep
 * sub-route (e.g. /home/automation/flows/builder) resolves to its parent section.
 * Returns null for routes not represented in the sidebar (utility/detail pages) —
 * callers treat null as "allowed" so those never get blocked.
 */
export function resolveIdFromEntries(pathname: string, entries: UrlIdEntry[]): string | null {
  let best: UrlIdEntry | null = null;
  for (const entry of entries) {
    if (pathMatchesUrl(pathname, entry.url)) {
      if (!best || entry.url.length > best.url.length) best = entry;
    }
  }
  return best ? best.id : null;
}

/**
 * Sections that stay reachable regardless of custom menu permissions:
 *  - 'settings' → General Settings (personal: profile, password, notifications,
 *    reminders) + Short Links. The admin-only sub-pages inside Account Settings
 *    (Audit Log, Billing, Team Mgmt, …) are still blocked by ROLE
 *    (RoleRoute / isAdminPreferencePath) — they just aren't menu-gated.
 *  - 'help' → Support pages (Getting Started, FAQs, Contact Support) — universal.
 * Without this, unchecking "Account Settings" in the dialog would also block a
 * member from their own profile/password page, which is never intended.
 */
export const ALWAYS_ALLOWED_SECTION_IDS = ['settings', 'help'];

/** True when the current route is reachable given a user's granted menu permissions. */
export function isRouteAllowed(pathname: string, entries: UrlIdEntry[], menuPermissions: string[]): boolean {
  const id = resolveIdFromEntries(pathname, entries);
  if (id == null) return true; // unmappable route -> never block
  if (ALWAYS_ALLOWED_SECTION_IDS.includes(id)) return true; // personal/help always reachable
  return menuPermissions.includes(id);
}

/**
 * First route the user IS allowed to open — the redirect target when they hit a
 * blocked URL. Falls back to '/home'; the caller guards against a redirect loop
 * by skipping the redirect when the target equals the current path.
 */
export function firstGrantedPathFrom(groups: Group[], menuPermissions: string[]): string {
  if (menuPermissions.includes('overview')) return '/home';
  if (menuPermissions.includes('inbox')) return '/home/inbox';
  for (const group of groups || []) {
    if (menuPermissions.includes(group.id)) {
      const first = group.secondaryItems?.[0]?.url;
      if (first) return first;
    }
  }
  return '/home';
}
